import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { Box, Flex, IconButton, Separator, Text } from '@chakra-ui/react';
import { UserMetadata } from '@supabase/supabase-js';
import Image from 'next/image';
import Link from 'next/link';
import { LuPanelLeftClose, LuPanelLeftOpen } from 'react-icons/lu';
import HeaderToolbarSlot from './HeaderToolbarSlot';
import { Avatar } from '../ui/avatar';
import { MenuContent, MenuItem, MenuItemGroup, MenuRoot, MenuSeparator, MenuTrigger } from '../ui/menu';
import { Tooltip } from '../ui/tooltip';
const KBE_DOCS_URL = 'https://docs.google.com/document/d/1LKDggOSZjAI0DW5KyNa4jtRbbbWe6_SHcj7dW-XOSvM/edit?usp=sharing';
type AppHeaderProps = {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  user: UserMetadata | null;
  onLogout: () => void;
  networkSelector?: ReactNode;
  viewSelector?: ReactNode;
  networkToolbar?: ReactNode;
  pinnedToolbar?: ReactNode;
  rightToolbar?: ReactNode;
  notificationBell?: ReactNode;
};
const ZoneSeparator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Separator borderColor="gray.300" height="24px" marginX={2} orientation="vertical" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const AppHeader = t0 => {
  const $ = _c(38);
  const {
    sidebarCollapsed,
    onToggleSidebar,
    user,
    onLogout,
    networkSelector,
    viewSelector,
    networkToolbar,
    pinnedToolbar,
    rightToolbar,
    notificationBell
  } = t0;
  const t1 = `${sidebarCollapsed ? "Expand" : "Collapse"} sidebar (⌘/Ctrl+B)`;
  const t2 = sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar";
  const t3 = !sidebarCollapsed;
  let t4;
  if ($[0] !== sidebarCollapsed) {
    t4 = sidebarCollapsed ? <LuPanelLeftOpen size={18} /> : <LuPanelLeftClose size={18} />;
    $[0] = sidebarCollapsed;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== onToggleSidebar || $[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = <IconButton aria-label={t2} aria-pressed={t3} size="sm" variant="ghost" onClick={onToggleSidebar}>{t4}</IconButton>;
    $[2] = onToggleSidebar;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t5) {
    t6 = <Tooltip content={t1} openDelay={200}>{t5}</Tooltip>;
    $[7] = t1;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Image priority={true} alt="Ancestralize" height={24} src="/company-logo.jpeg" style={{
      borderRadius: 4,
      objectFit: "contain"
    }} width={24} />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Text display={{
      base: "none",
      sm: "block"
    }} fontSize="md" fontWeight="bold" letterSpacing="wide">KBE</Text>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Flex alignItems="center" flexShrink={0} gap={2}>{t7}{t8}<Text color="gray.500" display={{
        base: "none",
        lg: "block"
      }} fontSize="sm">Ancestralize</Text></Flex>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== networkSelector || $[14] !== viewSelector) {
    t10 = (networkSelector || viewSelector) && <><ZoneSeparator /><Flex alignItems="center" flexShrink={0} gap={2} minW={0}>{networkSelector}{viewSelector}</Flex></>;
    $[13] = networkSelector;
    $[14] = viewSelector;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== networkToolbar || $[17] !== pinnedToolbar) {
    t11 = (networkToolbar || pinnedToolbar) && <Box display={{
      base: networkToolbar ? "block" : "none",
      lg: "block"
    }}><ZoneSeparator /></Box>;
    $[16] = networkToolbar;
    $[17] = pinnedToolbar;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== networkToolbar) {
    t12 = <HeaderToolbarSlot toolbar={networkToolbar} />;
    $[19] = networkToolbar;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== pinnedToolbar) {
    t13 = pinnedToolbar && <Flex alignItems="center" flexShrink={0} gap={2}>{pinnedToolbar}</Flex>;
    $[21] = pinnedToolbar;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Box flex="1" minW={0} />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== rightToolbar) {
    t15 = rightToolbar && <Flex alignItems="center" flexShrink={0} gap={2}>{rightToolbar}</Flex>;
    $[24] = rightToolbar;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== onLogout || $[27] !== user) {
    t16 = user && <MenuRoot><MenuTrigger as={IconButton} cursor="pointer" data-testid="avatar" rounded="full"><Avatar size="sm" src={user.picture as string} /></MenuTrigger><MenuContent minW="200px"><MenuItemGroup title={`Signed in as ${user.name ? user.name as string : ""}`}><Text color="gray.500" fontSize="sm" pb={2} px={3}>{user.email as string}</Text></MenuItemGroup><MenuSeparator /><MenuItem asChild={true} cursor="pointer" value="profile"><Link href="/profile">Profile</Link></MenuItem><MenuItem cursor="pointer" value="kbe-documentation" onClick={_temp}>KBE Documentation</MenuItem><MenuSeparator /><MenuItem cursor="pointer" value="logout" onClick={onLogout}>Logout</MenuItem></MenuContent></MenuRoot>;
    $[26] = onLogout;
    $[27] = user;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== notificationBell || $[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t15 || $[35] !== t16 || $[36] !== t6) {
    t17 = <Box bg="white" borderBottomColor="gray.200" borderBottomWidth="1px" flexShrink={0} height="48px" overflow="hidden" px={2}><Flex alignItems="center" gap={2} height="48px">{t6}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{notificationBell}{t16}</Flex></Box>;
    $[29] = notificationBell;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
    $[36] = t6;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
};
export default AppHeader;
function _temp() {
  return window.open(KBE_DOCS_URL, "_blank");
}